import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import { useExportOptions } from '@/hooks/useExportOptions';

import type { useGetCollectionTableProps } from './config';
import { buildImpactRatingRegisterToolbarConfig } from './registerToolbarConfig';
import type { ImpactRatingTableFields } from './types';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `getCounter(...)` → `(12)`). */
  counter?: string;
  tableProps: ReturnType<typeof useGetCollectionTableProps>;
}

/**
 * Object toolbar for the Impact Ratings register, mounted only when the
 * `object_toolbar` flag is on. Owning `useExportOptions` here (rather than on the
 * page) keeps it off the legacy `ExportButton` path — with the flag off this
 * component never renders, so the export options are computed once, by whichever
 * path is actually shown (CUS-155, mirroring the Indicators export-only register
 * CUS-150). The register has no `CustomisableRibbon`, so no ribbon filters are
 * wired; the overflow "Edit" group only appears if a ribbon relocates its editor
 * into the bridge store (never on this page today).
 */
export const ImpactRatingRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
}) => {
  const { t } = useTranslation(['common']);
  const [showPdfModal, setShowPdfModal] = useState(false);
  // Ribbon "Edit Filters" opener bridged up from CustomisableRibbon (relocate
  // mode). The register has no ribbon, so this stays null and the overflow
  // "Edit" group is omitted.
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<ImpactRatingTableFields>({
      tableProps,
      entityLabel: title,
      onShowCustomPdfModal,
    });

  const toolbarConfig = useMemo(
    () =>
      buildImpactRatingRegisterToolbarConfig({
        title,
        counter,
        exportOptions,
        editRibbon: ribbonConfig
          ? {
              groupLabel: t('edit'),
              label: t('objectToolbar.filterSettings'),
              onClick: ribbonConfig.openEditRibbon,
            }
          : undefined,
      }),
    [title, counter, exportOptions, ribbonConfig, t]
  );

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={true}
      />
    </>
  );
};
